/**
 * SS-4267 — what an Office Optimizer chat accepts as an attached file, checked in the
 * browser BEFORE anything is uploaded. The api checks the same things again (and is the
 * authority); these checks exist so a file that would be refused is never sent at all.
 */

export const ACCEPTED_EXTENSIONS = ["csv", "docx", "pdf"];
export const ACCEPT_ATTRIBUTE = ".csv,.docx,.pdf";
export const MAX_UPLOAD_BYTES = 10000000; // the api's /upload limit
export const MAX_UPLOAD_ROWS = 5000;

export const UNSUPPORTED_FILE_MESSAGE = "Only CSV, Word (.docx) and PDF files can be read.";
export const TOO_LARGE_MESSAGE = "This file is larger than 10 MB. A file can be up to 10 MB.";
export const rowLimitMessage = (rows) => `This file has ${rows} rows. A file can include up to 5,000 rows.`;

/** 'csv' | 'docx' | 'pdf', or null. */
export const extensionOf = (name) => {
  const value = String(name || "").toLowerCase();
  const ext = value.includes(".") ? value.split(".").pop() : "";
  return ACCEPTED_EXTENSIONS.includes(ext) ? ext : null;
};

/**
 * Data rows of a CSV (header excluded), by the api's parser rules: a quote opens a quoted
 * field only at the very start of the field, "" inside quotes is a literal quote, a quoted
 * field may span lines, and a line that is empty or whitespace-only (no separator, no
 * quoting) is not a row.
 */
export const countCsvDataRows = (input) => {
  const s = String(input || "").replace(/^\uFEFF/, "");
  let records = 0;
  let inQuotes = false;
  let field = "";
  let fieldsInRow = 0; // separators seen on this line
  let rowQuoted = false;
  let rowText = "";
  const endRow = () => {
    const blank = fieldsInRow === 0 && !rowQuoted && rowText.trim() === "";
    if (!blank) records += 1;
    field = "";
    fieldsInRow = 0;
    rowQuoted = false;
    rowText = "";
  };
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"' && field === "") { inQuotes = true; rowQuoted = true; continue; }
    if (c === ",") { fieldsInRow += 1; field = ""; continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      endRow();
      continue;
    }
    if (c === "\u0000") continue;
    field += c;
    rowText += c;
  }
  if (inQuotes) {
    // An unterminated quote would swallow the rest of the file as one row. The api then
    // counts by lines (po/uploads/limits.js), and so does this, so a malformed file is held
    // to the same limit on both sides.
    const lines = s.split(/\r\n|\r|\n/).filter((line) => line.trim() !== "");
    return Math.max(0, lines.length - 1);
  }
  if (field.length || fieldsInRow || rowQuoted || rowText) endRow();
  return Math.max(0, records - 1);
};

/** A file's text: UTF-8 when it is valid UTF-8, otherwise Windows-1252 (Excel's "CSV"). */
export const decodeCsv = (buffer) => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch (e) {
    return new TextDecoder("windows-1252").decode(buffer);
  }
};

const readAsArrayBuffer = (file) => {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Why this file cannot be attached, or null when it can.
 * @returns {Promise<string|null>}
 */
export const checkAttachment = async (file) => {
  if (!file) return UNSUPPORTED_FILE_MESSAGE;
  const ext = extensionOf(file.name);
  if (!ext) return UNSUPPORTED_FILE_MESSAGE;
  // the api's /upload (busboy) stops a file when it REACHES the limit: 10,000,000 bytes is refused
  if (file.size >= MAX_UPLOAD_BYTES) return TOO_LARGE_MESSAGE;
  if (ext === "csv") {
    const rows = countCsvDataRows(decodeCsv(await readAsArrayBuffer(file)));
    if (rows > MAX_UPLOAD_ROWS) return rowLimitMessage(rows);
  }
  return null;
};

/** A name safe to store: no path separators or control characters, extension kept. */
export const sanitizeFileName = (name) => {
  const raw = String(name || "file").split(/[\\/]/).pop();
  const ext = extensionOf(raw);
  const base = (ext ? raw.slice(0, -(ext.length + 1)) : raw)
    .replace(/[^\w .()-]+/g, "_")
    .replace(/\s+/g, " ")
    .replace(/\.{2,}/g, ".")
    .slice(0, 100)
    // no leading/trailing dots or spaces: "patients." + ".csv" must not become "patients..csv",
    // which the api refuses (a ".." anywhere in the stored name)
    .replace(/^[.\s]+|[.\s]+$/g, "") || "file";
  return ext ? `${base}.${ext}` : base;
};

const uuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** The stored name: '<uuid>-<sanitised name>' (flat — the api refuses anything else). */
export const storedFileName = (name) => `${uuid()}-${sanitizeFileName(name)}`;

const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

/** What to show for a stored name: the office's own file name, without the uuid. */
export const displayFileName = (fileName) => String(fileName || "").replace(UUID_PREFIX, "") || String(fileName || "");

/** A skipped-rows CSV the assistant produced for an upload (api names it so). */
export const isSkippedRowsBuffer = (buffer) => buffer?.type === "csv-file"
  && String(buffer?.fileName || "").startsWith("Skipped_rows_");

/**
 * The chat's messages without a recovered copy of an upload turn that is about to be shown
 * live: the user message carrying one of `storedNames` (a stored name is unique) and the
 * messages of the same chat entry (qa_id). Unchanged when there is no such copy.
 */
export const withoutRecoveredTurn = (list, storedNames) => {
  const names = new Set(storedNames || []);
  if (!names.size) return list;
  const recovered = (list || []).find((m) => m?.type === "user" && (m.q_buffers || []).some((b) => names.has(b.fileName)));
  if (!recovered) return list;
  const hasId = (m) => m.qa_id !== null && m.qa_id !== undefined;
  const qaId = hasId(recovered) ? String(recovered.qa_id) : null;
  return list.filter((m) => m !== recovered && !(qaId && m.type !== "user" && hasId(m) && String(m.qa_id) === qaId));
};

/**
 * Whether a chat entry fetched by recovery is an upload turn already on screen — shown live
 * before its entry id was known. Its stored file name is unique, so it is matched by that.
 */
export const isUploadTurnOnScreen = (entry, messages) => {
  const names = (entry?.q_buffers || []).map((b) => b?.fileName).filter(Boolean);
  if (!names.length) return false;
  return (messages || []).some((m) => m?.type === "user" && (m.q_buffers || []).some((b) => names.includes(b.fileName)));
};
